import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import Checklist from '@repo/datalayer/store/models/Checklist'
import ChecklistItem from '@repo/datalayer/store/models/ChecklistItem'
import { observer } from 'mobx-react-lite'
import { FileUploadButton, uploadFile } from './FileUploadButton'
import { FileViewDialog } from './FileViewDialog'
import { useTranslation } from 'react-i18next'
import { useState, useRef } from 'react'
import {
  Check,
  Edit,
  MoreHorizontal,
  Minus,
  SkipForward,
  Undo2,
  Trash2,
  Upload,
} from 'lucide-react'
import { EditItemDialog } from './EditItemDialog'
import { DescriptionFileViewDialog } from './DescriptionFileViewDialog'
import { useCurrentUser } from '@/auth'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { toast } from 'sonner'

function ItemCheckbox({
  item,
  onClick,
}: {
  item: ChecklistItem
  onClick: () => void
}) {
  return (
    <button
      data-test-id="checklist-item-checkbox"
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
      className={cn(
        'w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all duration-200',
        item.complete &&
          'bg-success border-success text-success-foreground',
        item.skipped && 'bg-muted border-muted-foreground/20',
        !item.complete &&
          !item.skipped &&
          'border-muted-foreground/30 hover:border-primary hover:bg-primary/5'
      )}
    >
      {item.complete && (
        <Check size={14} className="text-white" strokeWidth={3} />
      )}
      {item.skipped && (
        <Minus size={14} className="text-muted-foreground" strokeWidth={2} />
      )}
    </button>
  )
}

export const ChecklistItemView = observer(function ChecklistItemView({
  checklist,
  item,
}: {
  checklist: Checklist
  item: ChecklistItem
}) {
  const { t } = useTranslation()

  const handleToggleComplete = () => {
    const isCompleting = !item.complete && !item.skipped
    console.log({
      isCompleting,
      requiresMedia: item.requiresMedia,
      hasFile: item.hasFile,
    })
    if (isCompleting && item.requiresMedia && !item.hasFile) {
      toast.error(t('checklists.requiresMediaToComplete'))
      return
    }
    item.toggleComplete()
  }

  return (
    <div
      data-test-id="checklist-item"
      className="group transition-all duration-200 hover:bg-primary/5"
    >
      <div className="flex items-center gap-2 sm:gap-4 p-3 sm:p-4">
        {/* Checkbox */}
        {!checklist.complete && (
          <div className="flex-shrink-0">
            <ItemCheckbox item={item} onClick={handleToggleComplete} />
          </div>
        )}
        {checklist.complete && (
          <div className="flex-shrink-0">
            <div
              className={cn(
                'w-6 h-6 rounded-full flex items-center justify-center',
                item.complete &&
                  'bg-success text-success-foreground',
                item.skipped && 'bg-muted',
                !item.complete &&
                  !item.skipped &&
                  'border-2 border-muted-foreground/30'
              )}
            >
              {item.complete && (
                <Check size={14} className="text-white" strokeWidth={3} />
              )}
              {item.skipped && (
                <Minus
                  size={14}
                  className="text-muted-foreground"
                  strokeWidth={2}
                />
              )}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p
            data-test-id="checklist-item-name"
            className={cn(
              'text-sm font-medium leading-snug truncate sm:whitespace-normal',
              item.complete && 'line-through text-muted-foreground',
              item.skipped && 'line-through text-muted-foreground/50'
            )}
          >
            {item.name}
            {item.requiresMedia && (
              <span className="ml-2 inline-flex items-center rounded-full bg-warning/20 px-2 py-0.5 text-[10px] font-semibold text-foreground">
                {t('checklists.mediaRequired')}
              </span>
            )}
          </p>
          {item.description && (
            <p
              className="text-xs text-muted-foreground mt-0.5 sm:mt-1 hidden sm:block"
            >
              {item.description}
            </p>
          )}
        </div>

        {/* Actions */}
        {checklist.complete && <CompletedChecklistActions item={item} />}
        {!checklist.complete && (
          <ActiveChecklistActions
            checklist={checklist}
            item={item}
            showMore={false}
            setShowMore={() => {}}
          />
        )}
      </div>
    </div>
  )
})

export const CompletedChecklistActions = observer(
  function CompletedChecklistActions({ item }: { item: ChecklistItem }) {
    return (
      <div className="flex items-center gap-2">
        {item.hasFile && <FileViewDialog useIcon item={item} />}
      </div>
    )
  }
)

export const ActiveChecklistActions = observer(function ActiveChecklistActions({
  checklist,
  item,
  showMore,
  setShowMore,
}: {
  checklist: Checklist
  item: ChecklistItem
  showMore: boolean
  setShowMore: (show: boolean) => void
}) {
  const user = useCurrentUser()
  const { t } = useTranslation()
  const userIsCreator = checklist.creatorId === user.id
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    try {
      // Get presigned URL from server
      const data = await item.getUploadUrl(file.name)

      // Upload file to S3
      await uploadFile(file, data.url, () => {})

      // Record the file in the database using the server-returned filename
      await item.addFile(data.filename, file.type)
      item.markAsComplete()
      toast.success(t('checklists.uploadSuccess'))
    } catch (error) {
      console.error('upload failed', error)
      toast.error(t('checklists.uploadError'))
    } finally {
      // Clear the input for future uploads
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  return (
    <div className="flex items-center gap-2">
      {/* Hidden file input - must be outside dropdown to persist when dropdown closes */}
      {!item.hasFile && item.canUploadFile && (
        <input
          type="file"
          data-test-id="checklist-item-file-input"
          ref={fileInputRef}
          onChange={handleFileUpload}
          accept="image/*,video/*"
          className="hidden"
        />
      )}

      {/* File view button - always visible when file exists */}
      {item.hasFile && <FileViewDialog useIcon item={item} />}

      {/* Dropdown menu for all actions */}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            data-test-id="checklist-item-menu"
            variant="ghost"
            size="sm"
            className="h-8 w-8 sm:h-9 sm:w-9 p-0 opacity-60 hover:opacity-100 transition-opacity"
          >
            <MoreHorizontal size={16} className="sm:w-[18px] sm:h-[18px]" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          {/* Skip/Unskip action */}
          {item.skipped ? (
            <DropdownMenuItem
              data-test-id="checklist-item-unskip"
              onClick={() => item.toggleComplete()}
              className="cursor-pointer py-3"
            >
              <Undo2 className="mr-3 h-4 w-4" />
              {t('checklists.unskip')}
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              data-test-id="checklist-item-skip"
              onClick={() => item.setAsSkipped()}
              className="cursor-pointer py-3"
            >
              <SkipForward className="mr-3 h-4 w-4" />
              {t('checklists.skip')}
            </DropdownMenuItem>
          )}

          {/* Upload file action */}
          {!item.hasFile && item.canUploadFile && (
            <DropdownMenuItem
              data-test-id="checklist-item-upload"
              onClick={() => fileInputRef.current?.click()}
              className="cursor-pointer py-3"
            >
              <Upload className="mr-3 h-4 w-4" />
              {t('checklists.uploadFile')}
            </DropdownMenuItem>
          )}

          {/* Edit action - only for non-template checklists by creator */}
          {!checklist.templateId && userIsCreator && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-test-id="checklist-item-edit"
                className="cursor-pointer py-3"
                onClick={() => setEditDialogOpen(true)}
              >
                <Edit className="mr-3 h-4 w-4" />
                {t('edit')}
              </DropdownMenuItem>
            </>
          )}

          {/* Delete action - only for non-template checklists by creator */}
          {!checklist.templateId && userIsCreator && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setDeleteDialogOpen(true)}
                className="cursor-pointer py-3 text-destructive focus:text-destructive focus:bg-destructive/10"
              >
                <Trash2 className="mr-3 h-4 w-4" />
                {t('delete')}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Edit Dialog - controlled by state */}
      {!checklist.templateId && userIsCreator && (
        <EditItemDialog
          item={item}
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('checklists.deleteItemTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('checklists.deleteItemDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => checklist.deleteItem(item.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
})
